import {
    Box,
    Stack,
    Heading,
    Text,
    Container,
    VStack,
    Avatar,
    Button,
    HStack,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    useDisclosure
} from '@chakra-ui/react'
import { useRef } from 'react'
import AddCoParentDrawer from './AddCoParentDrawer'
import useParentSearch from '../hooks/useParentSearch'
import ParentSearchForm from './ParentSearchForm'
import ParentSearchResults from './ParentSearchResults'
import { useSuspenseQuery } from '@tanstack/react-query'
import { ParentsService } from '@/shared/api/rest/services/parents.service'
import { CoParentsService } from '@/shared/api/rest/services/co-parents.service'
import { ParentProfileCard } from './ParentProfileCard'
import { useCoParentSelection } from '../hooks/useCoParentSelection'
import PageContainer from '@/components/PageContainer'
import { PROTECTED_ROUTES } from '@/shared/config/routes'

export default function CoParent() {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const cancelRef = useRef()
    const { parents, loading, register, handleSubmit, errors } = useParentSearch()
    const { data } = useSuspenseQuery({
        queryKey: ['parent-profile-coparent'],
        queryFn: async () => {
            const parent = await ParentsService.me()
            const coParent = await CoParentsService.me().catch(() => null)
            return { findMyProfileAsParent: { ...parent, coParent } }
        }
    })
    const { findMyProfileAsParent } = data

    const { selectedCoParent, selectCoParent, addCoParent, isAdding, clearSelection } =
        useCoParentSelection()

    const handleConfirm = async () => {
        await addCoParent(findMyProfileAsParent._id)
        onClose()
        clearSelection()
    }

    return (
        <>
            <PageContainer
                routeConfig={PROTECTED_ROUTES['/user/parent/children-list']}
                headerText="Co-Parent & Spouse"
                subHeaderText="You can link your children to the same spouse by using
                            information"
                headerActions={
                    <Box>
                        {!findMyProfileAsParent.coParent && (
                            <AddCoParentDrawer parentId={findMyProfileAsParent._id} />
                        )}
                    </Box>
                }
            >
                <Stack direction={['column', 'row']} py={4}>
                    <ParentProfileCard profile={findMyProfileAsParent as any} />
                    <Container maxW="container.md">
                        <VStack spacing={6} alignItems="baseline">
                            <Heading as="h4" size="md">
                                Find your spouse&apos;s name
                            </Heading>
                            <ParentSearchForm
                                // @ts-expect-error react-hook-form onSubmit handler typing
                                onSubmit={handleSubmit}
                                register={register}
                                errors={errors}
                                loading={loading}
                            />
                            <ParentSearchResults
                                parents={parents}
                                loading={loading}
                                onSelectParent={(parent) => {
                                    selectCoParent(parent)
                                    onOpen()
                                }}
                            />
                            {selectedCoParent && (
                                <AlertDialog
                                    // @ts-expect-error Chakra AlertDialog ref prop typing
                                    leastDestructiveRef={cancelRef}
                                    isOpen={isOpen}
                                    onClose={onClose}
                                >
                                    <AlertDialogOverlay>
                                        <AlertDialogContent>
                                            <AlertDialogHeader
                                                fontSize="lg"
                                                fontWeight="bold"
                                            >
                                                Add Co-parent
                                            </AlertDialogHeader>

                                            <AlertDialogBody>
                                                {`Are you sure? You can't undo this action afterwards.`}

                                                <HStack my={3}>
                                                    <Avatar
                                                        name={`${selectedCoParent.firstName} ${selectedCoParent.lastName}`}
                                                        src={selectedCoParent?.picture}
                                                    />
                                                    <VStack align="start" spacing={0}>
                                                        <Text fontWeight="bold">
                                                            {selectedCoParent.firstName}{' '}
                                                            {selectedCoParent.lastName}
                                                        </Text>
                                                        <Text
                                                            fontSize="sm"
                                                            color="gray.500"
                                                        >
                                                            {
                                                                selectedCoParent.branch
                                                                    ?.name
                                                            }
                                                        </Text>
                                                    </VStack>
                                                </HStack>
                                            </AlertDialogBody>

                                            <AlertDialogFooter>
                                                <Button
                                                    colorScheme="gray"
                                                    // @ts-expect-error Chakra ref prop typing
                                                    ref={cancelRef}
                                                    onClick={() => {
                                                        onClose()
                                                        clearSelection()
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    colorScheme="brand"
                                                    onClick={handleConfirm}
                                                    isLoading={isAdding}
                                                    ml={3}
                                                >
                                                    Confirm
                                                </Button>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialogOverlay>
                                </AlertDialog>
                            )}
                        </VStack>
                    </Container>
                </Stack>
            </PageContainer>
        </>
    )
}
